import React from 'react';
import { ValueEntryModal } from '../../../../../../components/ValueEntryModal/ValueEntryModal';
import type { ValueEntryResult } from '../../../../../../components/ValueEntryModal/ValueEntryModal';

export type DiscountMode = 'amount' | 'percentage';
export type DiscountScope = 'all' | 'snapshot';

export type GlobalDiscountModalProps = {
  isOpen: boolean;
  /** remaining-to-pay minus tips — the percent basis and ceiling for any discount */
  remainingToPay: number;
  existingDiscountAmount?: number;
  isSaving?: boolean;
  onClose: () => void;
  /** discountAmount = computed capped amount; discountInput = raw user input value */
  onConfirm: (discountAmount: number, mode: DiscountMode, scope: DiscountScope, discountInput: number) => void;
  onRemoveDiscount?: () => void;
  /** non-managers can't remove a discount directly — show this instead of the remove button */
  removeDisabledMessage?: string;
};

const DISCOUNT_SCOPE = {
  allLabel: 'הנחה על כל ההזמנה',
  allSub: 'כולל מוצרים שיתווספו במשך',
  snapshotLabel: 'הנחה חד פעמית',
  snapshotSub: 'רק על הפריטים הנוכחיים בהזמנה',
};

// Thin wrapper over the shared ValueEntryModal — the global order discount. Persistence (scope-for-payments,
// percentage-vs-amount) is decided by the caller from the returned { mode, scope, rawInput, amount }.
const GlobalDiscountModalComponent: React.FC<GlobalDiscountModalProps> = ({
  isOpen,
  remainingToPay,
  existingDiscountAmount = 0,
  isSaving = false,
  onClose,
  onConfirm,
  onRemoveDiscount,
  removeDisabledMessage,
}) => (
  <ValueEntryModal
    isOpen={isOpen}
    title="הנחה כללית"
    navyHeader
    initialMode="amount"
    percentBase={remainingToPay}
    infoRightLabel="יתרה לתשלום"
    infoRightValue={remainingToPay}
    inputLabels={{ percentage: 'אחוז הנחה', amount: 'סכום הנחה' }}
    scope={DISCOUNT_SCOPE}
    maxPercent={100}
    maxAmount={remainingToPay}
    capPercentMessage="אחוז הנחה חייב להיות בין 0 ל-100"
    capAmountMessage="ההנחה גבוהה מהיתרה לתשלום"
    existingAmount={existingDiscountAmount}
    allowAddToExisting
    addToExistingLabel="הוסף להנחה הקיימת"
    overrideReplaceMessage="קיימת הנחה פעילה, הפעולה תדרוס את ההנחה הקיימת"
    overrideAddMessage="ההנחה החדשה תתווסף להנחה הקיימת"
    removeLabel="הסר את ההנחה הקיימת"
    removeDisabledMessage={removeDisabledMessage}
    dataTestId="global-discount"
    isSaving={isSaving}
    onClose={onClose}
    onConfirm={(result: ValueEntryResult) => onConfirm(result.amount, result.mode, result.scope, result.rawInput)}
    onRemove={onRemoveDiscount}
  />
);

export const GlobalDiscountModal = React.memo(GlobalDiscountModalComponent);
